/**
 * Component: Video Modal
 * -----------------------------------------------------------------------------
 * This component handles the display of a video modal, supporting both HTML5
 * videos and Wistia embedded videos.
 *
 * Usage:
 * - Add the following attributes to trigger elements:
 *   - For HTML5 video: `js-video-modal-trigger js-video-modal-src="{{ video_url }}"`
 *   - For Wistia video: `js-video-modal-trigger js-video-wistia-id="{{ wistia_url }}"`
 * - Modal layout should follow the provided structure.
 */

/** @const {string} CSS class to activate the modal. */
const ACTIVE_CLASS = 'is-active'

/** @const {string} CSS class to lock the body when modal is active. */
const LOCKED_CLASS = 'is-locked'

/** @typedef {Object} GlobalState
 *  @property {string | null} activeWistiaID - Currently active Wistia video ID.
 */

/** @type {GlobalState} */
const state = {
  activeWistiaID: '',
}

/** @typedef {Object} DOMSelectors
 *  @property {string} openModal - Selector for modal triggers.
 *  @property {string} closeModal - Selector for modal close buttons.
 *  @property {string} videoModal - Selector for the video modal container.
 *  @property {string} videoModalContent - Selector for modal content area.
 *  @property {string} videoElement - Selector for HTML5 video element within modal.
 */

/** @type {DOMSelectors} */
const selectors = {
  openModal: '[js-video-modal-trigger]',
  closeModal: '[js-video-modal-close]',
  videoModal: '[js-video-modal]',
  videoModalContent: '[js-video-modal-content]',
  videoElement: '[js-video-media]',
}

/** @typedef {Object} NodeElements
 *  @property {HTMLElement | null} videoModal - Modal container element.
 *  @property {HTMLVideoElement | null} videoElement - HTML5 video element.
 */

/** @type {NodeElements} */
const nodeElements = {
  videoModal: document.querySelector(selectors.videoModal),
  videoElement: document.querySelector(selectors.videoElement),
}

/**
 * Adds event listeners for opening and closing the video modal.
 */
function setEventListeners() {
  document.body.addEventListener('click', (e) => {
    if (!nodeElements.videoModal) {
      console.error(`${selectors.videoModal} element not found.`)

      return
    }

    const openPopupTarget = e.target.closest(selectors.openModal)
    const closePopupTarget = e.target.closest(selectors.closeModal)
    const videoContentTarget = e.target.closest(selectors.videoModalContent)

    const isOpened = Boolean(
      nodeElements.videoModal.classList.contains(ACTIVE_CLASS)
    )

    if (openPopupTarget && !isOpened) {
      const dynamicVideoSrc = openPopupTarget.getAttribute('js-video-modal-src')
      const wistiaVideoID = openPopupTarget.getAttribute('js-video-wistia-id')

      handleVideoSource(dynamicVideoSrc, wistiaVideoID)
      openModal()

      return
    }

    /**
     * Close the modal when clicking on the close button or outside the modal.
     */
    if (
      isOpened &&
      (closePopupTarget || (!closePopupTarget && !videoContentTarget))
    ) {
      pauseWistia(state.activeWistiaID)
      closeModal()
    }
  })
}

/**
 * Handles the setup of video sources for HTML5 or Wistia videos.
 * @param {string | null} dynamicVideoSrc - Source URL for HTML5 video.
 * @param {string | null} wistiaVideoID - ID for Wistia video.
 */
function handleVideoSource(dynamicVideoSrc, wistiaVideoID) {
  const existingWistiaVideo =
    nodeElements.videoModal?.querySelector('[js-wistia-video]')

  if (!nodeElements.videoElement) {
    console.error(`${selectors.videoElement} element not found.`, {
      dynamicVideoSrc,
      wistiaVideoID,
    })

    return
  }

  if (dynamicVideoSrc) {
    setupHTML5Video(dynamicVideoSrc, existingWistiaVideo)
  }

  if (wistiaVideoID) {
    setupWistiaVideo(wistiaVideoID, existingWistiaVideo)
  }
}

/**
 * Sets up an HTML5 video source in the modal.
 * @param {string} videoSrc - Source URL for the video.
 * @param {Element | null | undefined} wistiaVideo - Existing Wistia element.
 */
function setupHTML5Video(videoSrc, wistiaVideo) {
  const { videoElement } = nodeElements

  if (!videoElement) {
    console.error(`${selectors.videoElement} element not found.`, {
      videoElement,
      videoSrc,
      wistiaVideo,
    })

    return
  }

  videoElement.src = videoSrc
  videoElement.removeAttribute('hidden')
  wistiaVideo?.setAttribute('hidden', '')
  videoElement.play()
}

/**
 * Sets up a Wistia video in the modal.
 * @param {string} wistiaID - Wistia video ID.
 * @param {Element | null | undefined} existingWistia - Existing Wistia video element.
 */
function setupWistiaVideo(wistiaID, existingWistia) {
  nodeElements.videoElement?.setAttribute('hidden', '')

  if (existingWistia?.getAttribute('js-wistia-video') === wistiaID) {
    existingWistia.removeAttribute('hidden')
    playWistia(wistiaID)
  } else {
    replaceOrAddWistiaVideo(wistiaID, existingWistia)
  }
}

/**
 * Replaces or appends a Wistia video element to the modal.
 * @param {string} wistiaID - Wistia video ID.
 * @param {Element | null | undefined} existingWistia - Existing Wistia video element.
 */
function replaceOrAddWistiaVideo(wistiaID, existingWistia) {
  const newWistiaVideo = createWistiaElement(wistiaID)

  if (!newWistiaVideo) {
    return
  }

  if (existingWistia) {
    existingWistia.parentNode?.replaceChild(newWistiaVideo, existingWistia)
  } else {
    nodeElements.videoElement?.parentNode?.appendChild(newWistiaVideo)
  }

  state.activeWistiaID = wistiaID
  initWistiaVideo(wistiaID)
}

/**
 * Opens the video modal by adding the active class.
 */
function openModal() {
  nodeElements.videoModal?.classList.add(ACTIVE_CLASS)
  document.body.classList.add(LOCKED_CLASS)

  setScrollBarWidth()
}

/**
 * Close the video modal.
 *
 * @description Removes form errors and `is-active` class to hide the modal.
 */
function closeModal() {
  nodeElements.videoModal?.classList.remove(ACTIVE_CLASS)
  document.body.classList.remove(LOCKED_CLASS)
}

/**
 * Adjusts the scrollbar width to prevent layout shifts when locking the body.
 */
function setScrollBarWidth() {
  /**
   * Set actual scrollbar width as a variable.
   * - This is used to apply as `margin-right` when body is scroll locked.
   * - This prevents the entire body shifting to the right when overlay appears.
   */
  const scrollbarWidth = window.innerWidth - document.body.clientWidth

  document.documentElement.style.setProperty(
    '--body-scroll-bar-width',
    `${scrollbarWidth}px`
  )
}

/**
 * Creates a Wistia video element.
 * @param {string} wistiaID - Wistia video ID.
 * @returns {HTMLElement | null} - The Wistia video element.
 */
function createWistiaElement(wistiaID) {
  if (!wistiaID) {
    console.error(`Can't create Wistia Element: Missing ID.`, {
      wistiaID,
    })

    return null
  }

  const wistiaVideoEl = document.createElement('div')
  wistiaVideoEl.classList.add('wistia_embed', `wistia_async_${wistiaID}`)
  wistiaVideoEl.setAttribute('js-wistia-video', wistiaID)

  return wistiaVideoEl
}

/**
 * Initializes a Wistia video.
 * @param {string} wistiaID - Wistia video ID.
 */
function initWistiaVideo(wistiaID) {
  if (!wistiaID) {
    console.error(`Can't initialize Wistia: Missing ID.`, {
      wistiaID,
    })

    return
  }

  window._wq = window._wq || []

  _wq.push({
    id: wistiaID,
    onReady: (video) => {
      console.log('Wistia video is ready:', video, video.aspect())
      const existingWistiaVideo = nodeElements.videoModal?.querySelector(`[js-wistia-video="${wistiaID}"]`)

      if (existingWistiaVideo) {
        existingWistiaVideo.style.setProperty(
          '--aspect-ratio',
          video.aspect()
        )
      }

      video.play()
    },
  })
}

/**
 * Plays a Wistia video.
 * @param {string} wistiaID - Wistia video ID.
 */
function playWistia(wistiaID) {
  if (!wistiaID || !window.Wistia) {
    console.error(`Can't play Wistia video: Missing elements or ID.`, {
      wistiaID,
    })

    return
  }

  const wistiaVideo = Wistia.api(wistiaID)
  wistiaVideo?.play()
}

/**
 * Pauses a Wistia video.
 * @param {string} wistiaID - Wistia video ID.
 */
function pauseWistia(wistiaID) {
  if (!wistiaID || !window.Wistia) {
    console.error(`Can't pause Wistia video: Missing elements or ID.`, {
      wistiaID,
    })

    return
  }

  const wistiaVideo = Wistia.api(wistiaID)
  wistiaVideo?.pause()
}

/**
 * Initialize component.
 */
window.addEventListener('DOMContentLoaded', () => {
  setScrollBarWidth()
  setEventListeners()
})
